import { useState, useRef } from 'react';
import SandhillsReCAPTCHA from '@Components/Shared/SandhillsReCAPTCHA';
import { gtmPushEvent, sandhillsRequest, focusErroredField } from '@Components/Utilities/SandhillsUtility';
import TextField from '@Components/Shared/Controls/TextField';
import TranslationText from '@Components/Utilities/TranslationText';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import translator from '@Components/Utilities/Translator';
import SelectField from '@Components/Shared/Controls/SelectField';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import Modal from '@Components/Shared/Modals/Modal';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';

const FeedbackModal = (props) => {

    const routes = useRecoilValue(atoms.routes);

    const timeout = useRef(null);
    const inputReferences = useRef({});
    const recaptchaRef = useRef(null);

    const [captchaOpen, setCaptchaOpen] = useState(false);
    const [success, setSuccess] = useState(false);
    const [hideForm, setHideForm] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [disableModal, setDisableModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [formData, setFormData] = useState({
        Subject: props.SubjectDropDown ? props.SubjectDropDown[0].Value : '',
        Phone: props.Phone || '',
        PhonePlaceholder: props.PhonePlaceholder || '',
        BestTimeToCall: '',
        Email: props.Email || '',
        Company: props.Company || '',
        FirstName: props.FirstName || '',
        LastName: props.LastName || '',
        Address: props.Address || '',
        Address2: props.Address2 || '',
        Country: props.Country || '',
        City: props.City || '',
        Countries: props.GetCountryDropDown || [],
        State: props.State || '',
        States: props.GetStateDropDown || [],
        PostalCode: props.PostalCode || '', 
        Message: '',
        SerialNumberOrVIN: props.SerialNumberOrVIN || '',
        Manufacturer: props.Manufacturer || '',
        MediaID: props.Media ? props.Media[0]?.mediaId : ''
    });
    const isBA = formData.Subject === 'BecomeAuctioneer';
    const isDelete = props.SubjectDropDown && props.SubjectDropDown.length > 0 && props.SubjectDropDown[0].Value === 'RequestDataDeletion';

    const submitForm = (e) => {
        e.preventDefault();
        let rules = [];
        if (props.isSNVIN) {
            rules = [
                ruleRunner('SerialNumberOrVIN', 'Serial Number or VIN', ValidationRules.required),
                ruleRunner('Message', 'Message', ValidationRules.required),
                ruleRunner('Subject', 'Subject', ValidationRules.required)
            ]
        }
        else {
            rules = [
                ruleRunner('Email', 'Email', ValidationRules.requiredOr('Phone', 'Phone')),
                ruleRunner('Phone', 'Phone', ValidationRules.requiredOr('Email', 'Email')),
                ruleRunner('Message', 'Message', ValidationRules.required)
            ];

            if (formData.Subject && formData.Subject === 'BecomeAuctioneer') {
                rules.push(ruleRunner('Company', 'Company', ValidationRules.required));
                rules.push(ruleRunner('FirstName', 'First Name', ValidationRules.required));
                rules.push(ruleRunner('LastName', 'Last Name', ValidationRules.required));
                rules.push(ruleRunner('Address', 'Address', ValidationRules.required));
                rules.push(ruleRunner('Country', 'Country', ValidationRules.required));
                rules.push(ruleRunner('City', 'City', ValidationRules.required));
            } else if (formData.Subject && formData.Subject === 'RequestDataDeletion') {
                rules.push(ruleRunner('Email', 'Email', ValidationRules.required));
                rules.push(ruleRunner('FirstName', 'First Name', ValidationRules.required));
                rules.push(ruleRunner('LastName', 'Last Name', ValidationRules.required));
                rules.push(ruleRunner('Country', 'Country', ValidationRules.required));
                if (formData.States.length > 1) {
                    rules.push(ruleRunner('State', 'State', ValidationRules.required));
                }
            }

            if (formData.Email != '') {
                rules.push(ruleRunner('Email', translator.translate('sandhills.email'), ValidationRules.validEmail));
            }
        }
             
        let errors = run(formData, rules);

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);
        setSubmitted(false);

        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            setDisableModal(true);
            if (props.Captcha && recaptchaRef.current) {
                setCaptchaOpen(true);
                recaptchaRef.current.execute();
            } else {
                onSubmit();
            }
        }      
    };

    const onSubmit = (token) => {
        sandhillsRequest({
            url: routes.SubmitFeedback,
            method: 'post',
            data: {
                model: {
                    ...formData,
                    Captcha: {
                        Response: token
                    }
                }
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            setFormData(previousState => {
                return {
                    ...previousState,
                    Subject: props.SubjectDropDown ? props.SubjectDropDown[0].Value : '',
                    Phone: data?.Phone ? data?.Phone : '',
                    BestTimeToCall: data.BestTimeToCall || '',
                    Email: data?.Email ? data?.Email : '',
                    Company: data.Company || '',
                    FirstName: data.FirstName || '',
                    LastName: data.LastName || '',
                    Address: data.Address || '',
                    Address2: data.Address2 || '',
                    City: data.City || '',
                    Country: data.Country || '',
                    State: data.State || '',
                    PostalCode: data.PostalCode || '',
                    Message: data.Message || '',
                    SerialNumberOrVIN: data.SerialNumberOrVIN || '',
                    Manufacturer: data.Manufacturer || '',
                };
            });

            setSuccess(data.Success);
            setSubmitted(true);
            setDisableModal(false);
            setValidationErrors(data.ValidationErrors || {});
            focusErroredField(data.ValidationErrors, inputReferences);
            if (data.Success) {
                gtmPushEvent('contact-form', 'subject', formData.Subject);
                if (props.UseLeadsUXUpdate) {
                    setHideForm(true);
                }
                timeout.current = closeModalTimer();
            }

            if (props.Captcha && recaptchaRef.current) {
                setCaptchaOpen(false);
                recaptchaRef.current.reset();
            }
        });
    };

    const closeModalTimer = () => setTimeout(() => {
        props.toggleModal();
    }, 3000);

    const countryChange = (e) => {
        let country = e.target.value;
        setFormData(previousState => {
            return {
                ...previousState,
                Country: country
            };
        });
        sandhillsRequest({
            url: routes.GetStatesByCountry,
            data: {
                'CountryName': country,
                'DefaultText': translator.translate('sandhills.registration.selectastateprovince')
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            setFormData(previousState => {
                return {
                    ...previousState,
                    States: data
                };
            });
        });
        sandhillsRequest({
            url: routes.GetPhoneNumberPlaceholder,
            data: {
                'CountryName': country
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            setFormData(previousState => {
                return {
                    ...previousState,
                    PhonePlaceholder: data
                };
            });
        });
    };

    const onChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
        setValidationErrors(previousState => {
            return { ...previousState, [name]: '' };
        });
    };

    const closeModal = () => {
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        props.toggleModal();
    };

    const resetModal = () => {
        setDisableModal(false);
    };

    return (
        <Modal containerClassName='feedback-modal' onEscapeKeyClose={closeModal}>
            <Modal.Header onClose={closeModal}>
                <Modal.Title>
                    {props?.title ? props.title : props.isSNVIN ? translator.translate('sandhills.mobileapps.sendfeedback') : translator.translate('sandhills.tradepubwebsite.mvc.feedbackemail')}
                </Modal.Title>
                {props.UseLeadsUXUpdate && success &&
                    <p className='success'>{translator.translate('sandhills.dealerservices.messagesentsuccessfully')}!</p>}
            </Modal.Header>
            {isDelete &&
                <div style={{ paddingBottom: `0.75em`, marginTop: `-1.5em` }} className={(hideForm ? ' hide' : '')}>
                    <i style={{ fontSize: `0.875rem` }}>
                        <span style={{ color: `crimson`, fontWeight: `800` }}>WARNING:</span> This request will remove your ability to log in and/or bid on all of <a href={`https://www.sandhills.com/ourbrands`} style={{ textDecoration: `underline` }}>our trade and auction sites</a>. If you want to simply deactivate your account, ask a question about your account, alter email subscriptions, or change settings on our websites, please click <a href={props.ContactUsLink} style={{ textDecoration: `underline` }}>here</a>. If you'd like to delete the app itself, you can do so from the phone's home screen.
                    </i>
                </div>}
            <Modal.Body>
                {isBA &&
                    <><div className="CAPrivacyPolicyNotice" dangerouslySetInnerHTML={{ __html: props.BecomeAnAuctioneerPolicyNotice }} style={{ 'font-size': '.75em', 'margin-top': '10px' }} /><br></br></>
                }
                <p id='description-label' className='hidden'>Share your feedback</p>
                <form className={'contact-form-modal' + (hideForm ? ' hide' : '')} name='feedbackForm' onSubmit={submitForm}>
                    <ValidationErrors errors={validationErrors} />
                    <div className='form-main-content'>
                        <div className='left-col'>
                            {props.SubjectDropDown && props.SubjectDropDown.length > 0 &&
                                <SelectField
                                    name='Subject'
                                    value={formData.Subject}
                                    options={props.SubjectDropDown}
                                    label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.subject' />}
                                    id="subject-select"
                                    class='form-el'
                                    selectClass='select-dropdown'
                                    required={true}
                                    validationErrors={validationErrors.Subject}
                                    ref={(element) => inputReferences.current[`Subject`] = element}
                                    onFieldChanged={onChange}
                                />}
                            {!props.isSNVIN &&
                                <>
                                <TextField
                                    id='Phone'
                                    name='Phone'
                                    value={formData.Phone}
                                    placeholder={formData.PhonePlaceholder}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.phone' />}
                                    class='form-el'
                                    required={true}
                                    validationErrors={validationErrors.Phone}
                                    ref={(element) => inputReferences.current[`Phone`] = element}
                                    onFieldChanged={onChange}
                                />
                                <TextField
                                    id='BestTimeToCall'
                                    name='BestTimeToCall'
                                    value={formData.BestTimeToCall}
                                    placeholder={translator.translate('sandhills.enterthebesttimetocall')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.besttimetocall' />}
                                    class='form-el'
                                    required={false}
                                    validationErrors={validationErrors.BestTimeToCall}
                                    ref={(element) => inputReferences.current[`BestTimeToCall`] = element}
                                    onFieldChanged={onChange}
                                />
                                <TextField
                                    id='Email'
                                    name='Email'
                                    value={formData.Email}
                                    placeholder={translator.translate('sandhills.enteryouremailaddress')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.email' />}
                                    class='form-el'
                                    required={true}
                                    validationErrors={validationErrors.Email}
                                    ref={(element) => inputReferences.current[`Email`] = element}
                                    onFieldChanged={onChange}
                                />
                                <TextField
                                    id='Company'
                                    name='Company'
                                    value={formData.Company}
                                    placeholder={translator.translate('sandhills.entercompanyname')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.company' />}
                                    class='form-el'
                                    required={isBA}
                                    validationErrors={validationErrors.Company}
                                    ref={(element) => inputReferences.current[`Company`] = element}
                                    onFieldChanged={onChange}
                                />
                                <TextField
                                    id='FirstName'
                                    name='FirstName'
                                    value={formData.FirstName}
                                    placeholder={translator.translate('sandhills.enteryourfirstname')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.firstname' />}
                                    class='form-el'
                                    required={isBA || isDelete}
                                    validationErrors={validationErrors.FirstName}
                                    ref={(element) => inputReferences.current[`FirstName`] = element}
                                    onFieldChanged={onChange}
                                />
                                <TextField
                                    id='LastName'
                                    name='LastName'
                                    value={formData.LastName}
                                    placeholder={translator.translate('sandhills.enteryourlastname')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.lastname' />}
                                    class='form-el'
                                    required={isBA || isDelete}
                                    validationErrors={validationErrors.LastName}
                                    ref={(element) => inputReferences.current[`LastName`] = element}
                                    onFieldChanged={onChange}
                                />
                            </>
                            }
                            {props.isSNVIN &&
                                <>
                                    <TextField
                                        id='SerialNumberOrVIN'
                                        name='SerialNumberOrVIN'
                                        value={formData.SerialNumberOrVIN}
                                        placeholder='Enter your serial number/VIN'
                                        type='text'
                                        label='Serial Number or VIN'
                                        class='form-el'
                                        required={true}
                                        validationErrors={validationErrors.SerialNumberOrVIN}
                                        ref={(element) => inputReferences.current[`SerialNumberOrVIN`] = element}
                                        onFieldChanged={onChange}
                                    />
                                    <TextField
                                        id='Manufacturer'
                                        name='Manufacturer'
                                        value={formData.Manufacturer}
                                        placeholder='Manufacturer'
                                        type='text'
                                        label='Manufacturer'
                                        class='form-el'
                                        validationErrors={validationErrors.Manufacturer}
                                        ref={(element) => inputReferences.current[`Manufacturer`] = element}
                                        onFieldChanged={onChange}
                                    />
                                </>
                            }
                        </div>
                        <div className='right-col'>
                            {!props.isSNVIN &&
                                <TextField
                                    id='Address'
                                    name='Address'
                                    value={formData.Address}
                                    placeholder={translator.translate('sandhills.enteraddressline1')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.mobileapps.ios.physicaladdress' />}
                                    class='form-el'
                                    required={isBA}
                                    validationErrors={validationErrors.Address}
                                    ref={(element) => inputReferences.current[`Address`] = element}
                                    onFieldChanged={onChange}
                                />
                            }
                            {!props.isSNVIN &&
                                <TextField
                                    id='Address2'
                                    name='Address2'
                                    value={formData.Address2}
                                    placeholder={translator.translate('sandhills.enteraddressline2')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.addressline2' />}
                                    class='form-el'
                                    required={false}
                                    validationErrors={validationErrors.Address2}
                                    ref={(element) => inputReferences.current[`Address2`] = element}
                                    onFieldChanged={onChange}
                                />
                            }
                            {!props.isSNVIN &&
                                <TextField
                                    id='City'
                                    name='City'
                                    value={formData.City}
                                    placeholder={translator.translate('sandhills.city')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.city' />}
                                    class={'form-el' + (isBA ? '' : ' hidden')}
                                    required={isBA}
                                    validationErrors={validationErrors.City}
                                    ref={(element) => inputReferences.current[`City`] = element}
                                    onFieldChanged={onChange}
                                    htmlAutoComplete='chrome-off'
                                />
                            }
                            {formData.Countries.length > 1 && !props.isSNVIN &&
                                <SelectField
                                    name='Country'
                                    value={formData.Country}
                                    options={formData.Countries}
                                    label={<TranslationText tKey='sandhills.country' />}
                                    id="country-select"
                                    class='form-el'
                                    selectClass='select-dropdown'
                                    required={isBA || isDelete}
                                    validationErrors={validationErrors.Country}
                                    ref={(element) => inputReferences.current[`Country`] = element}
                                    onFieldChanged={countryChange}
                                />}
                            {formData.States.length > 1 && !props.isSNVIN ?
                                <SelectField
                                    name='State'
                                    value={formData.State}
                                    options={formData.States}
                                    label={<TranslationText tKey='sandhills.stateprovince' />}
                                    id="state-select"
                                    class='form-el'
                                    selectClass='select-dropdown'
                                    required={isDelete}
                                    validationErrors={validationErrors.State}
                                    ref={(element) => inputReferences.current[`State`] = element}
                                    onFieldChanged={onChange}
                                />
                                :
                                !props.isSNVIN &&
                                <TextField
                                    id='State'
                                    name='State'
                                    value={formData.State}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.stateprovince' />}
                                    class='form-el'
                                    required={false}
                                    validationErrors={validationErrors.State}
                                    ref={(element) => inputReferences.current[`State`] = element}
                                    onFieldChanged={onChange}
                                />}

                            {!props.isSNVIN &&
                                <TextField
                                    id='PostalCode'
                                    name='PostalCode'
                                    value={formData.PostalCode}
                                    placeholder={translator.translate('sandhills.enteryourpostalcode')}
                                    type='text'
                                    label={<TranslationText tKey='sandhills.postalcode' />}
                                    class='form-el'
                                    required={false}
                                    validationErrors={validationErrors.PostalCode}
                                    ref={(element) => inputReferences.current[`PostalCode`] = element}
                                    onFieldChanged={onChange}
                                />
                            }
                            <TextField
                                id='Message'
                                name='Message'
                                value={formData.Message}
                                placeholder={translator.translate('sandhills.tradesites.mvc.enteryourmessageupto500characters')}
                                type='textarea'
                                label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.message' />}
                                class='form-el text-area-container'
                                inputClass='text-area'
                                required={true}
                                validationErrors={validationErrors.Message}
                                ref={(element) => inputReferences.current[`Message`] = element}
                                onFieldChanged={onChange}
                            />
                        </div>
                    </div>
                    <div className='form-bottom'>
                        <div className='recaptcha-submit-container'>
                            {submitted &&
                                (success ?
                                    <div className='success'><TranslationText tKey='sandhills.dealerservices.messagesentsuccessfully' />!</div>
                                    :
                                    <div className='error'><TranslationText tKey='sandhills.errortryagain' /></div>)}
                            {!props.isSNVIN && <div className='captcha-placeholder'>
                                {props.Captcha &&
                                    <SandhillsReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={props.Captcha.Key}
                                        onChange={onSubmit}
                                        onCaptchaClosedBeforeCompletion={resetModal}
                                        captchaOpen={captchaOpen}
                                    />}
                            </div>}
                            <div class="CAPrivacyPolicyNotice" dangerouslySetInnerHTML={{ __html: props.FeedbackPolicyNotice }} style={{ 'font-size': '.75em', 'margin-top': '10px' }} />
                            <input className='ts-button recaptcha-submit-button button' type='submit' id='feedback-submit-button' name='feedback-submit-button' value={translator.translate('sandhills.tradepubwebsite.mvc.submit')} disabled={disableModal} />
                        </div>
                    </div>
                    {disableModal && <LoadingRing backdrop />}
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default FeedbackModal;